import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { outreachApi, errorText } from '../../utils/crmOutreach';

/*
 * CRM › Outreach: the Summary tab of a list and the dashboard of every campaign.
 * Numbers come from server/services/outreachSummary.js.
 */
const number = value => Number(value || 0).toLocaleString('en-IN');
const percent = value => value === null || value === undefined ? '—' : `${value}%`;
const share = (part, whole) => whole ? `${Math.round(part / whole * 100)}%` : '—';
const day = value => new Date(`${value}T00:00:00Z`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', timeZone: 'UTC' });
const date = value => value ? new Date(value).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '';
const dateTime = value => value ? new Date(value).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }) : '';
const plural = (count, one, many = `${one}s`) => `${number(count)} ${count === 1 ? one : many}`;

const STATES = { running: ['Running', 'green'], paused: ['Paused', 'amber'], draft: ['Not started', 'muted'] };
export const CampaignState = ({ campaign }) => {
    const [label, tone] = STATES[campaign?.state] || STATES.draft;
    return <span className={`crm-badge crm-tone-${tone}`}>{label}</span>;
};

function Stat({ label, value, hint, onClick }) {
    const body = <><div className="crm-stat-top"><span>{label}</span></div><strong>{value}</strong>{hint && <span className="crm-stat-hint">{hint}</span>}</>;
    return onClick ? <button type="button" className="crm-stat" onClick={onClick}>{body}</button> : <div className="crm-stat">{body}</div>;
}

// In the list → contacted → replied → verified, one bar per step on a shared scale (the whole list).
function Funnel({ funnel }) {
    const steps = [
        { label: 'In the list', value: funnel.inList },
        { label: 'Contacted', value: funnel.contacted, base: funnel.inList, of: 'of the list' },
        { label: 'Replied', value: funnel.replied, base: funnel.contacted, of: 'of contacted' },
        { label: 'Verified', value: funnel.verified, base: funnel.replied, of: 'of replied' }
    ];
    const max = Math.max(1, funnel.inList);
    return <ol className="outreach-funnel">{steps.map(step => <li key={step.label}>
        <div className="outreach-funnel-head"><span>{step.label}</span><span><strong>{number(step.value)}</strong>{step.of && <span className="crm-muted"> · {share(step.value, step.base)} {step.of}</span>}</span></div>
        <div className="outreach-funnel-bar" aria-hidden="true">{step.value > 0 && <span style={{ width: `${Math.max(1, step.value / max * 100)}%` }} />}</div>
    </li>)}</ol>;
}

// Round the axis up to 1, 2 or 5 × 10ⁿ so the gridlines land on clean numbers.
function niceScale(max, ticks = 3) {
    if (max <= 0) return { top: ticks, step: 1 };
    const raw = max / ticks, power = 10 ** Math.floor(Math.log10(raw));
    const step = [1, 2, 5, 10].map(factor => factor * power).find(value => value >= raw);
    return { top: Math.max(step, Math.ceil(max / step) * step), step: Math.max(1, step) };
}
// A column with a 4px rounded top and a square foot on the baseline.
const column = (x, y, width, height) => {
    const r = Math.min(4, width / 2, height);
    return `M${x},${y + height}V${y + r}Q${x},${y} ${x + r},${y}H${x + width - r}Q${x + width},${y} ${x + width},${y + r}V${y + height}Z`;
};
const SERIES = [{ key: 'campaign', label: 'Campaign emails sent', className: 'is-series-1' }, { key: 'vendorReplies', label: 'Vendor replies', className: 'is-series-2' }];

function DailyChart({ daily }) {
    const frame = useRef(null);
    const [width, setWidth] = useState(640);
    const [active, setActive] = useState(null);
    const [table, setTable] = useState(false);
    useLayoutEffect(() => {
        if (!frame.current || typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(([entry]) => setWidth(Math.max(280, Math.floor(entry.contentRect.width))));
        observer.observe(frame.current);
        return () => observer.disconnect();
    }, []);
    const empty = daily.every(row => !row.campaign && !row.vendorReplies);
    const height = 170, axis = 24, left = 32, top = 8, plot = height - top;
    const plotWidth = width - left - 4, band = plotWidth / daily.length;
    const bar = Math.max(3, Math.min(12, (band - 8) / 2));
    const { top: max, step } = niceScale(Math.max(...daily.map(row => Math.max(row.campaign, row.vendorReplies))));
    const y = value => top + plot - value / max * plot;
    const ticks = Array.from({ length: Math.round(max / step) + 1 }, (_, index) => index * step);
    const labelEvery = band < 30 ? 4 : band < 44 ? 2 : 1;
    const describe = row => `${day(row.day)}: ${plural(row.campaign, 'campaign email')} sent, ${plural(row.vendorReplies, 'vendor reply', 'vendor replies')}`;
    const tip = active === null ? null : daily[active];
    const onRight = active !== null && left + band * (active + 0.5) > width / 2;
    const tipStyle = active === null ? null : onRight ? { right: width - (left + band * active) + 6 } : { left: left + band * (active + 1) + 6 };

    return <div className="outreach-chart">
        <div className="outreach-chart-head">
            <ul className="outreach-legend" aria-label="Legend">{SERIES.map(series => <li key={series.key}><i className={series.className} />{series.label}</li>)}</ul>
            <button type="button" className="crm-text-button" onClick={() => setTable(value => !value)}>{table ? 'Show as chart' : 'Show as table'}</button>
        </div>
        <div ref={frame} className="outreach-chart-frame">
            {table ? <table className="crm-table outreach-chart-table"><thead><tr><th>Day</th>{SERIES.map(series => <th key={series.key}>{series.label}</th>)}</tr></thead>
                <tbody>{[...daily].reverse().map(row => <tr key={row.day}><td>{day(row.day)}</td>{SERIES.map(series => <td key={series.key}>{number(row[series.key])}</td>)}</tr>)}</tbody></table>
                : empty ? <p className="crm-muted outreach-chart-empty">No emails sent or received in the last {daily.length} days.</p>
                    : <>
                        <svg width={width} height={height + axis} role="img" aria-label={`Emails per day over the last ${daily.length} days. ${daily.filter(row => row.campaign || row.vendorReplies).map(describe).join('. ')}`}>
                            {ticks.map(value => <g key={value}>
                                <line className={value ? 'outreach-grid' : 'outreach-baseline'} x1={left} x2={width - 4} y1={y(value)} y2={y(value)} />
                                <text className="outreach-axis" x={left - 8} y={y(value) + 4} textAnchor="end">{value}</text>
                            </g>)}
                            {daily.map((row, index) => {
                                const x = left + band * index, pair = bar * 2 + 2, start = x + (band - pair) / 2;
                                return <g key={row.day}>
                                    {active === index && <rect className="outreach-hover-band" x={x + 1} y={top} width={band - 2} height={plot} rx="4" />}
                                    {SERIES.map((series, slot) => row[series.key] > 0 && <path key={series.key} className={`outreach-column ${series.className}`}
                                        d={column(start + slot * (bar + 2), y(row[series.key]), bar, top + plot - y(row[series.key]))} />)}
                                    {(daily.length - 1 - index) % labelEvery === 0 && <text className="outreach-axis" x={x + band / 2} y={height + 16} textAnchor="middle">{day(row.day)}</text>}
                                    <rect className="outreach-hit" x={x} y={top} width={band} height={plot} tabIndex={0} aria-label={describe(row)}
                                        onMouseEnter={() => setActive(index)} onMouseLeave={() => setActive(null)} onFocus={() => setActive(index)} onBlur={() => setActive(null)} />
                                </g>;
                            })}
                        </svg>
                        {tip && <div className="outreach-tooltip" style={tipStyle} role="status">
                            <strong className="outreach-tooltip-day">{day(tip.day)}</strong>
                            {SERIES.map(series => <span key={series.key}><i className={series.className} /><b>{number(tip[series.key])}</b> {series.label.toLowerCase()}</span>)}
                        </div>}
                    </>}
        </div>
    </div>;
}

/* ---------- One list ---------- */

export function CampaignSummary({ workspace, list, onOpenTab, onNotice }) {
    const [data, setData] = useState(null);
    const load = useCallback(() => outreachApi.listSummary(workspace, list.id).then(setData).catch(problem => onNotice(errorText(problem, 'The summary could not be loaded.'), true)), [workspace, list.id, onNotice]);
    useEffect(() => { load(); }, [load]);
    if (!data) return <p className="crm-muted" role="status">Loading summary…</p>;
    const summary = data.summary, campaign = summary.campaign, pacing = summary.pacing, stages = summary.stages, approvals = summary.approvals;
    const where = [
        ['Not contacted yet', stages.notContacted], ['Emailed, waiting for a reply', stages.emailed], ['Followed up, waiting for a reply', stages.followedUp],
        ['Replied', stages.replied], ['Stopped: no reply', stages.stoppedNoReply], ['Stopped: email bounced', stages.bounced], ['Stopped: other reason', stages.stoppedOther],
        ['No email or WhatsApp', stages.noEmail], ['Opted out', stages.optedOut]
    ];

    return <div className="outreach-summary">
        <section className="crm-panel outreach-summary-state">
            <div className="outreach-state">
                <CampaignState campaign={campaign} />
                <span>{campaign?.approved ? `Version ${campaign.version} approved` : 'This version needs the project lead’s approval'}</span>
                {campaign?.startedAt && <span className="crm-muted">Started {date(campaign.startedAt)}</span>}
                <div className="outreach-actions"><button type="button" className="crm-text-button" onClick={() => onOpenTab('campaign')}>Open campaign</button></div>
            </div>
            <p className="crm-muted outreach-pacing">{campaign?.state === 'running'
                ? <>Sent today: <strong>{number(campaign.sentToday)}</strong> of {number(campaign.dailyCap)} · Next email: <strong>{pacing.nextSendAt ? dateTime(pacing.nextSendAt) : 'none due'}</strong> · Still to send: <strong>{plural(pacing.firstEmailsLeft, 'first email')}</strong> and <strong>{plural(pacing.followUpsLeft, 'follow-up')}</strong></>
                : campaign?.state === 'paused' ? 'Paused: no campaign emails go out from this list until someone resumes it. Approved replies to vendors are still sent.'
                    : 'Not started: write the emails, have the project lead approve them, then start the campaign.'}</p>
        </section>

        <div className="outreach-kpis">
            <Stat label="Vendors" value={number(summary.vendors.total)} hint={`${number(summary.vendors.noEmail)} without email`} onClick={() => onOpenTab('members')} />
            <Stat label="Emails sent" value={number(summary.emails.campaign)} hint={`${number(summary.emails.first)} first, ${number(summary.emails.followUps)} follow-ups`} />
            <Stat label="Replied" value={number(summary.funnel.replied)} hint={`${plural(summary.emails.agentReplies, 'approved reply', 'approved replies')} sent`} />
            <Stat label="Reply rate" value={percent(summary.rates.reply)} hint={`of ${plural(summary.funnel.contacted, 'vendor')} contacted`} />
            <Stat label="Verified" value={number(summary.funnel.verified)} hint={`${number(summary.statuses.notInterested)} not interested`} />
            <Stat label="To approve" value={number(approvals.waiting)} hint={approvals.queued ? `${number(approvals.queued)} approved, sending soon` : 'Agent drafts waiting'} onClick={() => onOpenTab('approvals')} />
        </div>

        {summary.waitingVendors > 0 && <p className="outreach-warning" role="status">{plural(summary.waitingVendors, 'Verified vendor is', 'Verified vendors are')} waiting for a person to answer or take them over.</p>}

        <section className="crm-panel outreach-summary-card">
            <div className="crm-section-heading"><div><h3>Emails per day</h3><p>Campaign emails sent and vendor replies received, last {summary.daily.length} days. Automatic replies and bounces are not counted.</p></div></div>
            <DailyChart daily={summary.daily} />
        </section>

        <div className="outreach-summary-columns">
            <section className="crm-panel outreach-summary-card">
                <div className="crm-section-heading"><div><h3>From list to verified</h3><p>How many vendors reached each step.</p></div></div>
                <Funnel funnel={summary.funnel} />
            </section>
            <section className="crm-panel outreach-summary-card">
                <div className="crm-section-heading"><div><h3>Where vendors are now</h3><p>Every vendor in the list is in exactly one row.</p></div></div>
                <dl className="outreach-where">{where.map(([label, value]) => <React.Fragment key={label}><dt className={value ? '' : 'crm-muted'}>{label}</dt><dd className={value ? '' : 'crm-muted'}>{number(value)}</dd></React.Fragment>)}</dl>
                <h4 className="outreach-where-title">Agent replies</h4>
                <dl className="outreach-where">
                    <dt>Waiting for approval</dt><dd>{number(approvals.waiting)}</dd>
                    <dt>Being drafted</dt><dd>{number(approvals.drafting)}</dd>
                    <dt>Approved, sending soon</dt><dd>{number(approvals.queued)}</dd>
                    <dt>Sent</dt><dd>{number(approvals.sent)}</dd>
                    <dt>Rejected or taken over</dt><dd>{number(approvals.rejected + approvals.takenOver)}</dd>
                </dl>
            </section>
        </div>
    </div>;
}

/* ---------- Every campaign ---------- */

export function OutreachDashboard({ workspace, onOpen, onNotice }) {
    const [data, setData] = useState(null);
    useEffect(() => {
        let alive = true;
        setData(null);
        outreachApi.dashboard(workspace).then(result => { if (alive) setData(result); }).catch(problem => onNotice(errorText(problem, 'The campaign dashboard could not be loaded.'), true));
        return () => { alive = false; };
    }, [workspace, onNotice]);
    if (!data) return <p className="crm-muted" role="status">Loading campaigns…</p>;
    const totals = data.totals;
    return <div className="outreach-summary">
        <div className="crm-list-heading"><div><h2>Campaigns</h2><p>Every list you manage{workspace === 'test' ? ' in the Test workspace' : ''}. Choose a list for its full summary.</p></div></div>
        <div className="outreach-kpis">
            <Stat label="Running" value={number(totals.running)} hint={`${number(totals.paused)} paused, ${plural(totals.lists, 'list')} in all`} />
            <Stat label="Vendors" value={number(totals.vendors)} hint={`${number(totals.contacted)} contacted`} />
            <Stat label="Emails sent" value={number(totals.campaignEmails)} hint={`${plural(totals.agentReplies, 'approved reply', 'approved replies')} sent`} />
            <Stat label="Replied" value={number(totals.replied)} hint={`Reply rate ${percent(totals.replyRate)}`} />
            <Stat label="Verified" value={number(totals.verified)} hint="Interested and ready to work" />
            <Stat label="To approve" value={number(totals.waiting)} hint="Agent drafts waiting" />
        </div>
        <section className="crm-panel outreach-summary-card">
            {data.lists.length ? <div className="crm-table-scroll"><table className="crm-table outreach-campaign-table">
                <thead><tr><th>List</th><th>Campaign</th><th>Vendors</th><th>Emails sent</th><th>Replied</th><th>Verified</th><th>To approve</th></tr></thead>
                <tbody>{data.lists.map(row => <tr key={row.id}>
                    <td><button type="button" className="crm-text-button outreach-campaign-name" onClick={() => onOpen(row.id)}>{row.name}</button><span className="outreach-note">{row.projectName}</span></td>
                    <td><CampaignState campaign={row.campaign} /></td>
                    <td>{number(row.vendors)}<span className="outreach-note">{number(row.contacted)} contacted</span></td><td>{number(row.campaignEmails)}</td>
                    <td>{number(row.replied)}{row.replyRate !== null && <span className="outreach-note">{percent(row.replyRate)} reply rate</span>}</td><td>{number(row.verified)}</td>
                    <td>{row.waiting ? <span className="outreach-count">{number(row.waiting)}</span> : <span className="crm-muted">0</span>}</td>
                </tr>)}</tbody>
            </table></div> : <p className="crm-muted outreach-pad">No lists yet. Create one with New list.</p>}
        </section>
    </div>;
}
